import { useEffect, useMemo, useRef, useState } from 'react';
import {
  clearGoogleMapsInstanceListeners,
  isGoogleMapsMissingApiKeyError,
  loadGoogleMapsScript,
  type GoogleGeocoderResult,
  type GoogleLatLng,
  type GoogleLatLngLiteral,
  type GoogleMapInstance,
  type GoogleMapMouseEvent,
  type GoogleMapsListener,
  type GoogleMarkerInstance,
  type GooglePolygonInstance,
} from '~/app/lib/google-maps';
import type { PolygonPerimeter } from '@evimo/types';

export type AreaBoundaryReferenceCoordinates = {
  latitude: number;
  longitude: number;
};

type UseAreaBoundaryPickerMapParams = {
  addressQuery: string | null;
  selectedPerimeter: PolygonPerimeter | null;
  overlayPerimeter?: PolygonPerimeter | null;
  referenceCoordinates?: AreaBoundaryReferenceCoordinates | null;
  disabled: boolean;
  missingKeyText: string;
  geocodeFailedText: string;
  loadFailedText: string;
  onPerimeterChangeAction: (perimeter: PolygonPerimeter | null) => void;
};

function toLatLngLiteral(result: GoogleGeocoderResult): GoogleLatLngLiteral {
  return {
    lat: result.geometry.location.lat(),
    lng: result.geometry.location.lng(),
  };
}

function getPolygonCenter(perimeter: PolygonPerimeter): GoogleLatLngLiteral {
  const total = perimeter.reduce(
    (accumulator, point) => ({
      lat: accumulator.lat + point.lat,
      lng: accumulator.lng + point.lng,
    }),
    { lat: 0, lng: 0 },
  );

  return {
    lat: total.lat / perimeter.length,
    lng: total.lng / perimeter.length,
  };
}

function toPerimeterPoint(point: GoogleLatLng): GoogleLatLngLiteral {
  return {
    lat: point.lat(),
    lng: point.lng(),
  };
}

async function geocodeAddress(address: string): Promise<GoogleLatLngLiteral> {
  const googleMaps = await loadGoogleMapsScript();
  const geocoder = new googleMaps.maps.Geocoder();

  return new Promise<GoogleLatLngLiteral>((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status !== 'OK' || !results?.[0]) {
        reject(new Error('GEOCODE_FAILED'));
        return;
      }

      resolve(toLatLngLiteral(results[0]));
    });
  });
}

export function useAreaBoundaryPickerMap({
  addressQuery,
  selectedPerimeter,
  overlayPerimeter,
  referenceCoordinates,
  disabled,
  missingKeyText,
  geocodeFailedText,
  loadFailedText,
  onPerimeterChangeAction,
}: UseAreaBoundaryPickerMapParams) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMapInstance | null>(null);
  const referenceMarkerRef = useRef<GoogleMarkerInstance | null>(null);
  const perimeterPointMarkerRefs = useRef<GoogleMarkerInstance[]>([]);
  const selectedPolygonRef = useRef<GooglePolygonInstance | null>(null);
  const overlayPolygonRef = useRef<GooglePolygonInstance | null>(null);
  const clickListenerRef = useRef<GoogleMapsListener | null>(null);
  const perimeterListenersRef = useRef<GoogleMapsListener[]>([]);
  const currentPerimeterRef = useRef<PolygonPerimeter | null>(
    selectedPerimeter,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLocating, setIsLocating] = useState(false);

  const normalizedQuery = useMemo(
    () => addressQuery?.trim() ?? '',
    [addressQuery],
  );

  useEffect(() => {
    currentPerimeterRef.current = selectedPerimeter;
  }, [selectedPerimeter]);

  useEffect(() => {
    if (disabled || !mapContainerRef.current) {
      return;
    }

    let isCancelled = false;

    const syncEditablePerimeter = (): void => {
      const polygon = selectedPolygonRef.current;

      if (!polygon) {
        return;
      }

      const path = polygon.getPath();
      const nextPerimeter = Array.from(
        { length: path.getLength() },
        (_, index) => toPerimeterPoint(path.getAt(index)),
      );

      currentPerimeterRef.current =
        nextPerimeter.length >= 3 ? nextPerimeter : null;
      onPerimeterChangeAction(nextPerimeter.length >= 3 ? nextPerimeter : null);
    };

    setIsLocating(true);

    void loadGoogleMapsScript()
      .then(async (googleMaps) => {
        if (isCancelled || !mapContainerRef.current) {
          return;
        }

        setErrorMessage(null);

        if (!mapRef.current) {
          mapRef.current = new googleMaps.maps.Map(mapContainerRef.current, {
            center: { lat: 46.2276, lng: 2.2137 },
            zoom: 5,
            disableDefaultUI: false,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
            draggableCursor: 'crosshair',
            draggingCursor: 'grabbing',
          });
        }

        const mapInstance = mapRef.current;

        if (!mapInstance) {
          return;
        }

        perimeterListenersRef.current.forEach((listener) => listener.remove());
        perimeterListenersRef.current = [];
        clickListenerRef.current?.remove();

        perimeterPointMarkerRefs.current.forEach((marker) => {
          marker.setMap(null);
          clearGoogleMapsInstanceListeners(marker);
        });
        perimeterPointMarkerRefs.current = [];

        if (referenceCoordinates && mapRef.current) {
          const position = {
            lat: referenceCoordinates.latitude,
            lng: referenceCoordinates.longitude,
          };

          if (!referenceMarkerRef.current) {
            referenceMarkerRef.current = new googleMaps.maps.Marker({
              map: mapInstance,
              position,
              draggable: false,
            });
          } else {
            referenceMarkerRef.current.setPosition(position);
          }
        } else if (referenceMarkerRef.current) {
          referenceMarkerRef.current.setMap(null);
          referenceMarkerRef.current = null;
        }

        if (overlayPolygonRef.current) {
          overlayPolygonRef.current.setMap(null);
          overlayPolygonRef.current = null;
        }

        if (overlayPerimeter && overlayPerimeter.length >= 3 && mapInstance) {
          overlayPolygonRef.current = new googleMaps.maps.Polygon({
            map: mapInstance,
            paths: overlayPerimeter,
            editable: false,
            draggable: false,
            clickable: false,
            strokeColor: '#38bdf8',
            strokeOpacity: 0.85,
            strokeWeight: 2,
            fillColor: '#7dd3fc',
            fillOpacity: 0.18,
          });
        }

        if (selectedPolygonRef.current) {
          selectedPolygonRef.current.setMap(null);
          selectedPolygonRef.current = null;
        }

        if (selectedPerimeter?.length) {
          perimeterPointMarkerRefs.current = selectedPerimeter.map(
            (point, index) =>
              new googleMaps.maps.Marker({
                map: mapInstance,
                position: point,
                draggable: false,
                zIndex: 1000 + index,
              }),
          );
        }

        if (selectedPerimeter && selectedPerimeter.length >= 3 && mapInstance) {
          selectedPolygonRef.current = new googleMaps.maps.Polygon({
            map: mapInstance,
            paths: selectedPerimeter,
            editable: true,
            draggable: false,
            strokeColor: '#2563eb',
            strokeOpacity: 0.95,
            strokeWeight: 3,
            fillColor: '#60a5fa',
            fillOpacity: 0.24,
          });

          const path = selectedPolygonRef.current.getPath();

          perimeterListenersRef.current = [
            path.addListener('insert_at', syncEditablePerimeter),
            path.addListener('remove_at', syncEditablePerimeter),
            path.addListener('set_at', syncEditablePerimeter),
          ];
        }

        clickListenerRef.current = mapInstance.addListener(
          'click',
          (event: GoogleMapMouseEvent) => {
            const clickedLatLng = event.latLng;

            if (!clickedLatLng) {
              return;
            }

            const nextPoint = {
              lat: clickedLatLng.lat(),
              lng: clickedLatLng.lng(),
            };
            const nextPerimeter = [
              ...(currentPerimeterRef.current ?? []),
              nextPoint,
            ];

            currentPerimeterRef.current = nextPerimeter;

            // Create a marker immediately for visual feedback
            const newMarker = new googleMaps.maps.Marker({
              map: mapInstance,
              position: nextPoint,
              draggable: false,
              zIndex: 1000 + nextPerimeter.length,
            });
            perimeterPointMarkerRefs.current.push(newMarker);

            // Create or update the polygon if we have at least 3 points
            if (nextPerimeter.length >= 3) {
              if (selectedPolygonRef.current) {
                selectedPolygonRef.current.setMap(null);
              }
              selectedPolygonRef.current = new googleMaps.maps.Polygon({
                map: mapInstance,
                paths: nextPerimeter,
                editable: true,
                draggable: false,
                strokeColor: '#2563eb',
                strokeOpacity: 0.95,
                strokeWeight: 3,
                fillColor: '#60a5fa',
                fillOpacity: 0.24,
              });

              // Add listeners for polygon editing
              perimeterListenersRef.current.forEach((l) => l.remove());
              const path = selectedPolygonRef.current.getPath();
              perimeterListenersRef.current = [
                path.addListener('insert_at', syncEditablePerimeter),
                path.addListener('remove_at', syncEditablePerimeter),
                path.addListener('set_at', syncEditablePerimeter),
              ];
            }

            onPerimeterChangeAction(nextPerimeter);
          },
        );

        const anchorCenter = selectedPerimeter?.length
          ? getPolygonCenter(selectedPerimeter)
          : overlayPerimeter?.length
            ? getPolygonCenter(overlayPerimeter)
            : referenceCoordinates
              ? {
                  lat: referenceCoordinates.latitude,
                  lng: referenceCoordinates.longitude,
                }
              : null;

        if (anchorCenter && mapRef.current) {
          mapRef.current.setCenter(anchorCenter);
          // Use zoom 16 when we have reference coordinates but no perimeter yet
          // for better visibility when starting to draw
          const targetZoom = selectedPerimeter?.length
            ? 15
            : referenceCoordinates
              ? 16
              : 13;
          mapRef.current.setZoom(targetZoom);
          setIsLocating(false);
          return;
        }

        if (!normalizedQuery) {
          setIsLocating(false);
          return;
        }

        try {
          const geocodedCenter = await geocodeAddress(normalizedQuery);

          if (isCancelled || !mapRef.current) {
            return;
          }

          mapRef.current.setCenter(geocodedCenter);
          mapRef.current.setZoom(12);
        } catch (error) {
          if (isCancelled) {
            return;
          }

          setErrorMessage(
            error instanceof Error && error.message === 'GEOCODE_FAILED'
              ? geocodeFailedText
              : loadFailedText,
          );
        } finally {
          if (!isCancelled) {
            setIsLocating(false);
          }
        }
      })
      .catch((error: Error) => {
        if (isCancelled) {
          return;
        }

        setErrorMessage(
          isGoogleMapsMissingApiKeyError(error)
            ? missingKeyText
            : loadFailedText,
        );
        setIsLocating(false);
      });

    return () => {
      isCancelled = true;
      clickListenerRef.current?.remove();
      perimeterListenersRef.current.forEach((listener) => listener.remove());
      perimeterListenersRef.current = [];
      perimeterPointMarkerRefs.current.forEach((marker) => {
        marker.setMap(null);
        clearGoogleMapsInstanceListeners(marker);
      });
      perimeterPointMarkerRefs.current = [];
      clearGoogleMapsInstanceListeners(referenceMarkerRef.current);
      clearGoogleMapsInstanceListeners(selectedPolygonRef.current);
      clearGoogleMapsInstanceListeners(overlayPolygonRef.current);
      clearGoogleMapsInstanceListeners(mapRef.current);
    };
  }, [
    disabled,
    geocodeFailedText,
    loadFailedText,
    missingKeyText,
    normalizedQuery,
    onPerimeterChangeAction,
    overlayPerimeter,
    referenceCoordinates,
    selectedPerimeter,
  ]);

  return {
    errorMessage,
    isLocating,
    mapContainerRef,
  };
}
